import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { toast } from "react-toastify";
import StarRating from "../../components/StarRating";
import { uploadImageToCloudinary } from "../../services/cloudinary";

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState(""); // "hide" or "show"

  const [dynamicBrands, setDynamicBrands] = useState([]);

  const emptyProduct = {
    name: "",
    price: "",
    costPrice: "",
    originalPrice: "",
    stock: "",
    discount: "",
    soldCount: "",
    category: "",
    description: "",
    shortDescription: "",
    image: "",
    images: [],
    brand: "",
    sizes: [],
    slug: "",
    rating: "",
    reviewCount: "",
    colors: [],
    subcategory: "",
    gifts: [],
    specifications: {},
    isSecondHand: false,
  };

  useEffect(() => {
    fetchProducts();
    fetchBrands();

    // Listen for brand updates
    const handleBrandsUpdated = () => fetchBrands();
    window.addEventListener("brands_updated", handleBrandsUpdated);
    return () =>
      window.removeEventListener("brands_updated", handleBrandsUpdated);
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, categoryFilter, products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get("/products?limit=9999&includeHidden=true&includeAll=true");
      console.log("✅ Products:", response.data);
      setProducts(response.data.products || []);
      setLoading(false);
    } catch (error) {
      console.error("❌ Error fetching products:", error);
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get("/settings/brands_config");
      console.log("DEBUG: AdminProducts response raw:", response.data);

      const extractBrands = (data) => {
        if (!data) return null;
        if (Array.isArray(data)) {
          const brandsEntry = data.find(
            (item) => item && (item.Key === "brands" || item.key === "brands"),
          );
          if (
            brandsEntry &&
            Array.isArray(brandsEntry.Value || brandsEntry.value)
          ) {
            return brandsEntry.Value || brandsEntry.value;
          }
          if (data.length > 0 && typeof data[0] === "string") return data;
          if (data.length > 0) return extractBrands(data[0]);
          return null;
        }
        if (data.brands && Array.isArray(data.brands)) return data.brands;
        if (data.Brands && Array.isArray(data.Brands)) return data.Brands;
        if (data.value) return extractBrands(data.value);
        if (
          (data.Key === "brands" || data.key === "brands") &&
          Array.isArray(data.Value || data.value)
        ) {
          return data.Value || data.value;
        }
        return null;
      };

      const brandListArr = extractBrands(response.data);

      if (brandListArr && brandListArr.length > 0) {
        console.log("DEBUG: AdminProducts extracted brands:", brandListArr);
        setDynamicBrands(brandListArr);
      } else {
        console.log("DEBUG: AdminProducts no brands found");
      }
    } catch (error) {
      console.error("Lỗi khi tải thương hiệu:", error);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    if (categoryFilter !== "all") {
      filtered = filtered.filter((p) => p.category === categoryFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.category?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredProducts(filtered);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const handleAdd = () => {
    setSelectedProduct({
      ...emptyProduct,
      sizes: "",
      gifts: "",
    });
    setShowAddModal(true);
  };

  const handleEdit = (product) => {
    setSelectedProduct({
      ...product,
      images: product.images || [],
      sizes: Array.isArray(product.sizes)
        ? product.sizes.join(", ")
        : product.sizes || "",
      brand: product.brand || "",
      subcategory: product.subcategory || "",
      colors: product.colors || [],
      gifts: Array.isArray(product.gifts)
        ? product.gifts.join(", ")
        : product.gifts || "",
      specifications: product.specifications || {},
      shortDescription: product.shortDescription || "",
      isSecondHand: product.isSecondHand || false,
    });
    setShowEditModal(true);
  };

  const handleDelete = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const handleUploadImage = async (file) => {
    if (!file) return null;
    try {
      setIsUploading(true);
      const url = await uploadImageToCloudinary(file);
      setIsUploading(false);
      return url;
    } catch (error) {
      setIsUploading(false);
      toast.error(
        "Lỗi khi tải ảnh lên. Vui lòng kiểm tra lại cấu hình Cloudinary.",
      );
      console.error(error);
      return null;
    }
  };

  const prepareProductData = async () => {
    const slug = selectedProduct.name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    // Convert strings back to clean arrays
    const sizes =
      typeof selectedProduct.sizes === "string"
        ? selectedProduct.sizes
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s !== "")
        : selectedProduct.sizes || [];

    const gifts =
      typeof selectedProduct.gifts === "string"
        ? selectedProduct.gifts
            .split(",")
            .map((g) => g.trim())
            .filter((g) => g !== "")
        : selectedProduct.gifts || [];

    return {
      ...selectedProduct,
      slug,
      sizes,
      gifts,

      price: parseInt(selectedProduct.price) || 0,
      costPrice: parseInt(selectedProduct.costPrice) || 0,
      originalPrice: parseInt(selectedProduct.originalPrice) || 0,
      stock: parseInt(selectedProduct.stock) || 0,
      discount: parseInt(selectedProduct.discount) || 0,
      soldCount: parseInt(selectedProduct.soldCount) || 0,
      rating: parseFloat(selectedProduct.rating) || 5,
      reviewCount: parseInt(selectedProduct.reviewCount) || 0,
    };
  };

  const updateProductField = React.useCallback((field, value) => {
    setSelectedProduct((prev) => {
      if (!prev) return prev;

      let finalValue = value;

      // Chỉ cho phép số cho các trường này
      const numericFields = [
        "price",
        "costPrice",
        "originalPrice",
        "stock",
        "discount",
        "soldCount",
        "reviewCount",
      ];
      if (numericFields.includes(field)) {
        // Loại bỏ tất cả ký tự không phải số
        finalValue = value.toString().replace(/[^0-9]/g, "");
      }

      // Riêng rating cho phép số thập phân
      if (field === "rating") {
        finalValue = value.toString().replace(/[^0-9.]/g, "");
        // Đảm bảo chỉ có một dấu chấm
        const dots = finalValue.match(/\./g);
        if (dots && dots.length > 1) {
          finalValue = finalValue.replace(/\.+$/, "");
        }
      }

      let newState = { ...prev, [field]: finalValue };

      // Logic tính toán 3 trường: price, originalPrice, discount
      const p = parseFloat(newState.price) || 0;
      const op = parseFloat(newState.originalPrice) || 0;
      const d = parseFloat(newState.discount) || 0;

      if (field === "originalPrice" || field === "price") {
        // Có Giá gốc & Giá bán -> Tính % Giảm giá
        if (op > 0 && p > 0) {
          newState.discount = Math.round(((op - p) / op) * 100).toString();
        }
      } else if (field === "discount") {
        // Có Giá gốc & % Giảm giá -> Tính Giá bán
        if (op > 0) {
          newState.price = Math.round(
            op * (1 - (parseFloat(finalValue) || 0) / 100),
          ).toString();
        }
      }

      return newState;
    });
  }, []);

  const addProduct = async () => {
    try {
      setLoading(true);
      const productData = await prepareProductData();
      await api.post("/admin/products", productData);

      console.log("✅ Product added");
      fetchProducts();
      setShowAddModal(false);
      toast.success("Thêm sản phẩm thành công!");
    } catch (error) {
      console.error("❌ Add error:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Có lỗi xảy ra!";
      toast.error(`Thêm sản phẩm thất bại: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const saveProduct = async () => {
    try {
      setLoading(true);
      const productData = await prepareProductData();
      await api.put(`/admin/products/${selectedProduct._id}`, productData);

      console.log("✅ Product updated");
      fetchProducts();
      setShowEditModal(false);
      toast.success("Cập nhật sản phẩm thành công!");
    } catch (error) {
      console.error("❌ Update error:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Có lỗi xảy ra!";
      toast.error(`Cập nhật sản phẩm thất bại: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/products/${selectedProduct._id}`);
      console.log("✅ Product deleted");
      fetchProducts();
      setShowDeleteModal(false);
      toast.success("Xóa sản phẩm thành công!");
    } catch (error) {
      console.error("❌ Delete error:", error);
      toast.error("Xóa sản phẩm thất bại!");
    }
  };

  const handleToggleVisibility = async (product) => {
    try {
      const response = await api.put(
        `/admin/products/${product._id}/toggle-visibility`,
      );
      const newStatus = response.data.isHidden;

      // Update local state for immediate feedback
      setProducts((prev) =>
        prev.map((p) =>
          p._id === product._id ? { ...p, isHidden: newStatus } : p,
        ),
      );

      toast.success(`${newStatus ? "Đã ẩn" : "Đã hiện"} sản phẩm thành công!`);
    } catch (error) {
      console.error("❌ Toggle visibility error:", error);
      toast.error("Không thể thay đổi trạng thái hiển thị!");
    }
  };

  const handleBulkToggleVisibility = async (action) => {
    try {
      setLoading(true);
      await api.put("/admin/products/bulk-visibility", { action });
      toast.success(
        `Đã ${action === "hide" ? "ẩn" : "hiện"} toàn bộ sản phẩm thành công!`,
      );
      fetchProducts();
      setShowBulkModal(false);
    } catch (error) {
      console.error("❌ Bulk toggle error:", error);
      toast.error("Không thể cập nhật trạng thái hàng loạt!");
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    "all",
    "SecondHand",
    ...(dynamicBrands.length > 0
      ? dynamicBrands
      : [
          "NIKE",
          "ADIDAS",
          "MLB",
          "CONVERSE",
          "BALENCIAGA",
          "PUMA",
          "VANS",
          "NEW BALANCE",
          "FILA",
          "GUCCI",
          "LV",
          "JORDAN BRAND",
          "ASICS",
          "RICK OWENS",
          "TIMBERLAND",
        ]),
  ];

  const renderProductForm = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-xl mb-4">
        <div>
          <h4 className="font-bold text-yellow-800 dark:text-yellow-400">Sản phẩm SecondHand</h4>
          <p className="text-xs text-yellow-700 dark:text-yellow-500">Đánh dấu nếu đây là sản phẩm đã qua sử dụng</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input 
            type="checkbox" 
            className="sr-only peer"
            checked={selectedProduct?.isSecondHand || false}
            onChange={(e) => updateProductField("isSecondHand", e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 dark:peer-focus:ring-yellow-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-yellow-500"></div>
        </label>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Tên sản phẩm *
        </label>
        <input
          type="text"
          value={selectedProduct?.name || ""}
          onChange={(e) => updateProductField("name", e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Nhập tên sản phẩm"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Giá bán *
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={selectedProduct?.price ?? ""}
            onChange={(e) => updateProductField("price", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Giá gốc (Thị trường)
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={selectedProduct?.originalPrice ?? ""}
            onChange={(e) =>
              updateProductField("originalPrice", e.target.value)
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2 text-blue-600">
            Giá vốn (Giá nhập) *
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={selectedProduct?.costPrice ?? ""}
            onChange={(e) => updateProductField("costPrice", e.target.value)}
            className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50/30"
            placeholder="0"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2 text-green-600">
            Lợi nhuận dự kiến / SP
          </label>
          <div className="w-full px-4 py-2 bg-green-50 border border-green-100 rounded-lg font-black text-green-700">
            {formatCurrency(
              (selectedProduct?.price || 0) - (selectedProduct?.costPrice || 0),
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Số lượng *
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={selectedProduct?.stock ?? ""}
            onChange={(e) => updateProductField("stock", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Giảm giá (%)
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={selectedProduct?.discount ?? ""}
            onChange={(e) => updateProductField("discount", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0"
            min="0"
            max="100"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Số lượng đã bán
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={selectedProduct?.soldCount ?? ""}
            onChange={(e) => updateProductField("soldCount", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0"
            min="0"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Đánh giá (1-5 sao) *
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={selectedProduct?.rating ?? ""}
            onChange={(e) => {
              updateProductField("rating", e.target.value);
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="5.0"
          />
          <div className="mt-2 flex items-center gap-2">
            <StarRating rating={selectedProduct?.rating || 5} size="w-4 h-4" />
            <span className="text-xs text-gray-500">
              ({selectedProduct?.rating || 5})
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Số lượt đánh giá
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={selectedProduct?.reviewCount ?? ""}
            onChange={(e) => updateProductField("reviewCount", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0"
          />
          <p className="text-xs text-gray-500 mt-2">
            Hiển thị: ⭐⭐⭐⭐⭐ ({selectedProduct?.reviewCount || 0})
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Danh mục *
          </label>
          <select
            value={selectedProduct?.category || ""}
            onChange={(e) => updateProductField("category", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">-- Chọn danh mục --</option>
            {categories
              .filter((c) => c !== "all")
              .map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Thương hiệu
          </label>
          <input
            type="text"
            value={selectedProduct?.brand || ""}
            onChange={(e) => updateProductField("brand", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Nhập tên thương hiệu (Puma, Nike...)"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Dòng sản phẩm (Subcategory)
          </label>
          <input
            type="text"
            value={selectedProduct?.subcategory || ""}
            onChange={(e) => updateProductField("subcategory", e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="VD: ALPHA, BOOST, JORDAN..."
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Màu sắc & Hình ảnh tương ứng
        </label>
        <div className="space-y-3">
          {(selectedProduct?.colors || []).map((colorObj, idx) => (
            <div
              key={idx}
              className="flex gap-2 items-start bg-gray-50 p-3 rounded-lg border border-gray-200"
            >
              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  value={colorObj.name || ""}
                  onChange={(e) => {
                    const newColors = [...selectedProduct.colors];
                    newColors[idx].name = e.target.value;
                    updateProductField("colors", newColors);
                  }}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  placeholder="Tên màu (VD: Đen, Trắng)"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={colorObj.image || ""}
                    onChange={(e) => {
                      const newColors = [...selectedProduct.colors];
                      newColors[idx].image = e.target.value;
                      updateProductField("colors", newColors);
                    }}
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                    placeholder="URL hình ảnh cho màu này"
                  />
                  <input
                    id={`color-image-upload-${idx}`}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const url = await handleUploadImage(file);
                        if (url) {
                          const newColors = [...selectedProduct.colors];
                          newColors[idx].image = url;
                          updateProductField("colors", newColors);
                        }
                      }
                    }}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      document
                        .getElementById(`color-image-upload-${idx}`)
                        .click()
                    }
                    className="bg-white border border-gray-300 text-gray-600 px-2 py-1.5 rounded hover:bg-gray-50 text-xs"
                    title="Tải ảnh lên"
                  >
                    💻
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  const newColors = selectedProduct.colors.filter(
                    (_, i) => i !== idx,
                  );
                  updateProductField("colors", newColors);
                }}
                className="bg-red-100 text-red-600 p-1.5 rounded hover:bg-red-200 transition"
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const newColors = [
                ...(selectedProduct.colors || []),
                { name: "", image: "" },
              ];
              updateProductField("colors", newColors);
            }}
            className="text-blue-600 font-semibold text-sm hover:underline flex items-center gap-1"
          >
            + Thêm màu sắc mới
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Quà tặng kèm (Cách nhau bằng dấu phẩy)
        </label>
        <input
          type="text"
          value={selectedProduct?.gifts || ""}
          onChange={(e) => updateProductField("gifts", e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Ví dụ: Chai khử mùi, Đôi vớ"
        />
        <div className="mt-2 flex flex-wrap gap-2">
          {(typeof selectedProduct?.gifts === "string"
            ? selectedProduct.gifts.split(",")
            : selectedProduct?.gifts || []
          )
            .filter((g) => g && g.trim() !== "")
            .map((gift, idx) => (
              <span
                key={idx}
                className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold"
              >
                🎁 {gift.trim()}
              </span>
            ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Kích thước (Cách nhau bằng dấu phẩy)
        </label>
        <input
          type="text"
          value={selectedProduct?.sizes || ""}
          onChange={(e) => updateProductField("sizes", e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Ví dụ: 38, 39, 40, 41"
        />
        <div className="mt-2 flex flex-wrap gap-2">
          {(typeof selectedProduct?.sizes === "string"
            ? selectedProduct.sizes.split(",")
            : selectedProduct?.sizes || []
          )
            .filter((s) => s && s.trim() !== "")
            .map((size, idx) => (
              <span
                key={idx}
                className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold"
              >
                {size.trim()}
              </span>
            ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Mô tả ngắn (Hiển thị gần giá sản phẩm)
        </label>
        <textarea
          value={selectedProduct?.shortDescription || ""}
          onChange={(e) =>
            updateProductField("shortDescription", e.target.value)
          }
          rows="3"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
          placeholder="Nhập mô tả ngắn gọn, thường là các điểm nổi bật (xuống dòng để tạo danh sách có dấu tích)"
        ></textarea>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Mô tả chi tiết (Hiển thị trong Tab)
        </label>
        <textarea
          value={selectedProduct?.description || ""}
          onChange={(e) => updateProductField("description", e.target.value)}
          rows="6"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[150px]"
          placeholder="Nhập mô tả sản phẩm chi tiết (xuống dòng để trình bày đẹp hơn)"
        ></textarea>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Thông số kỹ thuật (Danh sách chi tiết)
        </label>
        <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
          {Object.entries(selectedProduct?.specifications || {}).map(
            ([key, value], idx) => (
              <div key={idx} className="flex gap-3">
                <input
                  type="text"
                  value={key}
                  onChange={(e) => {
                    const newSpecs = { ...selectedProduct.specifications };
                    const newKey = e.target.value;
                    if (newKey !== key) {
                      delete newSpecs[key];
                      newSpecs[newKey] = value;
                    }
                    updateProductField("specifications", newSpecs);
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 text-sm font-semibold"
                  placeholder="Tên thông số (VD: Chất liệu)"
                />
                <input
                  type="text"
                  value={value}
                  onChange={(e) => {
                    const newSpecs = { ...selectedProduct.specifications };
                    newSpecs[key] = e.target.value;
                    updateProductField("specifications", newSpecs);
                  }}
                  className="flex-[2] px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 text-sm"
                  placeholder="Giá trị (VD: Da cao cấp)"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newSpecs = { ...selectedProduct.specifications };
                    delete newSpecs[key];
                    updateProductField("specifications", newSpecs);
                  }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                >
                  ✕
                </button>
              </div>
            ),
          )}
          <button
            type="button"
            onClick={() => {
              const newSpecs = {
                ...(selectedProduct.specifications || {}),
                "": "",
              };
              updateProductField("specifications", newSpecs);
            }}
            className="text-blue-600 font-bold text-sm hover:underline flex items-center gap-1 mt-2"
          >
            + Thêm thông số mới
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Hình ảnh chính *
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={selectedProduct?.image || ""}
            onChange={(e) => updateProductField("image", e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="URL hình ảnh chính"
          />
          <input
            id="main-image-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (file) {
                const url = await handleUploadImage(file);
                if (url) updateProductField("image", url);
              }
            }}
          />

          <button
            type="button"
            onClick={() => document.getElementById("main-image-upload").click()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm whitespace-nowrap"
          >
            💻 Tải từ máy
          </button>
        </div>
        {selectedProduct?.image && (
          <div className="relative inline-block mt-1">
            <img
              src={selectedProduct.image}
              alt="Main"
              className="w-24 h-24 object-cover rounded border shadow-sm"
            />
            <button
              type="button"
              onClick={() => updateProductField("image", "")}
              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Các hình ảnh khác (Gallery)
        </label>
        <div className="space-y-3">
          {(selectedProduct?.images || []).map((imgUrl, idx) => (
            <div key={idx} className="flex gap-2">
              <input
                type="text"
                value={imgUrl}
                onChange={(e) => {
                  const newImages = [...selectedProduct.images];
                  newImages[idx] = e.target.value;
                  updateProductField("images", newImages);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="URL hình ảnh khác"
              />
              <input
                id={`other-image-upload-${idx}`}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const url = await handleUploadImage(file);
                    if (url) {
                      const newImages = [...selectedProduct.images];
                      newImages[idx] = url;
                      updateProductField("images", newImages);
                    }
                  }
                }}
              />

              <button
                type="button"
                onClick={() =>
                  document.getElementById(`other-image-upload-${idx}`).click()
                }
                className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition text-sm border border-gray-300"
              >
                💻
              </button>
              <button
                type="button"
                onClick={() => {
                  const newImages = selectedProduct.images.filter(
                    (_, i) => i !== idx,
                  );
                  updateProductField("images", newImages);
                }}
                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition"
              >
                ✕
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                const newImages = [...(selectedProduct.images || []), ""];
                updateProductField("images", newImages);
              }}
              className="text-blue-600 font-semibold text-sm hover:underline flex items-center gap-1"
            >
              + Thêm ô nhập URL
            </button>
            <span className="text-gray-300">|</span>
            <input
              id="multi-image-upload"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files);
                if (files.length > 0) {
                  const newImages = [...(selectedProduct.images || [])];
                  for (const file of files) {
                    const url = await handleUploadImage(file);
                    if (url) newImages.push(url);
                  }
                  updateProductField("images", newImages);
                }
              }}
            />

            <button
              type="button"
              onClick={() =>
                document.getElementById("multi-image-upload").click()
              }
              className="text-green-600 font-semibold text-sm hover:underline flex items-center gap-1"
            >
              📁 Tải nhiều ảnh từ máy
            </button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {(selectedProduct?.images || [])
            .filter((url) => url)
            .map((url, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={url}
                  alt={`Other ${idx}`}
                  className="w-16 h-16 object-cover rounded border shadow-sm"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newImages = selectedProduct.images.filter(
                      (_, i) => i !== idx,
                    );
                    updateProductField("images", newImages);
                  }}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[8px] opacity-0 group-hover:opacity-100 transition"
                >
                  ✕
                </button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8 bg-gray-50 min-h-screen font-inter">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-3xl font-black text-slate-800 tracking-tight">
            Sản phẩm
          </h1>
          <p className="text-[10px] sm:text-sm text-slate-400 font-bold uppercase tracking-widest mt-1">
            Kho hàng: {products.length} mẫu giày
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center justify-center gap-2 px-6 py-2.5 sm:py-3.5 bg-blue-600 text-white rounded-2xl font-black text-sm hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 active:scale-95"
        >
          <span className="material-symbols-outlined text-[20px]">
            add_circle
          </span>
          Thêm mẫu mới
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <button
          onClick={() => {
            setBulkAction("hide");
            setShowBulkModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold text-xs hover:bg-slate-200 transition-all border border-slate-200 shadow-sm"
        >
          <span className="material-symbols-outlined text-[18px]">visibility_off</span>
          Ẩn tất cả sản phẩm
        </button>
        <button
          onClick={() => {
            setBulkAction("show");
            setShowBulkModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl font-bold text-xs hover:bg-emerald-100 transition-all border border-emerald-100 shadow-sm"
        >
          <span className="material-symbols-outlined text-[18px]">visibility</span>
          Hiện tất cả sản phẩm
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-6 border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-[3] md:flex-[4] lg:flex-[5] relative md:min-w-[400px]">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">
              search
            </span>
            <input
              type="text"
              placeholder="Tìm kiếm theo tên sản phẩm, thương hiệu..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm font-bold transition-all placeholder:text-slate-300"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar -mx-1 px-1">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-4 py-2 rounded-xl text-[10px] sm:text-xs font-black whitespace-nowrap transition-all uppercase tracking-wider ${
                  categoryFilter === cat
                    ? "bg-slate-800 text-white shadow-lg shadow-slate-200"
                    : "bg-gray-100 text-slate-400 hover:bg-gray-200"
                }`}
              >
                {cat === "all" ? "Tất cả" : cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-4 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">
                  Sản phẩm
                </th>
                <th className="px-4 py-4 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest hidden sm:table-cell">
                  Danh mục
                </th>
                <th className="px-4 py-4 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">
                  Giá & Kho
                </th>
                <th className="px-4 py-4 text-right text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    className="py-20 text-center text-slate-300 font-bold uppercase text-[10px] tracking-widest"
                  >
                    Trống trải
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr
                    key={product._id}
                    className={`hover:bg-blue-50/10 transition-colors group ${product.isHidden ? "opacity-50 grayscale-[0.5]" : ""}`}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="relative shrink-0">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl object-cover border border-gray-100 group-hover:scale-105 transition-transform"
                          />
                          {product.discount > 0 && (
                            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-black px-1 rounded-full border border-white">
                              -{product.discount}%
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-black text-slate-800 truncate max-w-[140px] sm:max-w-xs">
                            {product.name}
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                              {product.brand || product.category}
                            </p>
                            {product.isHidden && (
                              <span className="text-[8px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter">
                                Đang ẩn
                              </span>
                            )}
                            {product.isSecondHand && (
                              <span className="text-[8px] bg-amber-500 text-white px-1.5 py-0.5 rounded font-black uppercase tracking-tighter flex items-center gap-0.5">
                                <span className="material-symbols-outlined text-[10px]">recycling</span>
                                SecondHand
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap hidden sm:table-cell">
                      <span className="text-[10px] font-black text-slate-400 bg-slate-50 border border-slate-100 px-2 py-1 rounded-lg uppercase">
                        {product.category}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <p className="text-xs sm:text-sm font-black text-slate-900">
                        {formatCurrency(product.price)}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span
                          className={`text-[9px] sm:text-[10px] font-black uppercase ${product.stock <= 5 ? "text-red-500" : "text-green-600"}`}
                        >
                          Kho: {product.stock}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleToggleVisibility(product)}
                          className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl transition-all ${
                            product.isHidden
                              ? "text-gray-400 hover:bg-gray-100"
                              : "text-emerald-500 hover:bg-emerald-50"
                          }`}
                          title={
                            product.isHidden ? "Hiện sản phẩm" : "Ẩn sản phẩm"
                          }
                        >
                          <span className="material-symbols-outlined text-[20px]">
                            {product.isHidden ? "visibility_off" : "visibility"}
                          </span>
                        </button>
                        <button
                          onClick={() => handleEdit(product)}
                          className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                        >
                          <span className="material-symbols-outlined text-[20px]">
                            edit_square
                          </span>
                        </button>
                        <button
                          onClick={() => handleDelete(product)}
                          className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-xl transition-all"
                        >
                          <span className="material-symbols-outlined text-[20px]">
                            delete
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals - Mobile Drawer Style */}
      {(showEditModal || showAddModal) && (
        <div className="fixed inset-0 z-[100] flex sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm overflow-hidden">
          <div className="bg-white w-full max-w-4xl h-full sm:h-auto sm:max-h-[90vh] sm:rounded-[32px] overflow-hidden shadow-2xl animate-modalUp flex flex-col">
            <div className="p-4 sm:p-6 bg-slate-900 text-white flex justify-between items-center shrink-0">
              <h3 className="text-sm sm:text-lg font-black uppercase tracking-widest flex items-center gap-3">
                <span
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${showEditModal ? "bg-blue-500" : "bg-green-500"}`}
                >
                  <span className="material-symbols-outlined text-white">
                    {showEditModal ? "edit" : "add"}
                  </span>
                </span>
                {showEditModal ? "Chỉnh sửa model" : "Thêm mẫu mới"}
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setShowAddModal(false);
                }}
                className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center bg-slate-800"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-8 no-scrollbar bg-white">
              <div className="max-w-3xl mx-auto">{renderProductForm()}</div>
            </div>

            <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-100 flex gap-3 shrink-0">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setShowAddModal(false);
                }}
                className="flex-1 py-3.5 px-6 font-black text-xs sm:text-sm text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-all sm:hidden md:block"
              >
                Hủy bỏ
              </button>
              <button
                onClick={showEditModal ? saveProduct : addProduct}
                disabled={loading}
                className="flex-[2] py-3.5 sm:py-4 px-6 bg-slate-900 text-white rounded-2xl font-black text-xs sm:text-sm uppercase tracking-widest hover:bg-black active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-xl shadow-slate-200"
              >
                {loading ? (
                  <span className="material-symbols-outlined animate-spin">
                    refresh
                  </span>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-[20px]">
                      {showEditModal ? "save" : "add_task"}
                    </span>
                    {showEditModal ? "Cập nhật dữ liệu" : "Xác nhận thêm"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] w-full max-w-sm overflow-hidden shadow-2xl animate-modalUp p-6 sm:p-8 text-center border border-gray-100">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-6 border-2 border-red-100">
              <span className="material-symbols-outlined text-[32px]">
                delete_sweep
              </span>
            </div>
            <h3 className="text-lg font-black text-slate-800 mb-2 uppercase tracking-widest">
              Loại bỏ mẫu?
            </h3>
            <p className="text-xs sm:text-sm text-slate-400 font-bold mb-8 leading-relaxed">
              Dữ liệu của mẫu{" "}
              <span className="text-slate-800">{selectedProduct?.name}</span> sẽ
              bị xóa vĩnh viễn khỏi hệ thống.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-3 text-slate-400 font-black text-[10px] uppercase tracking-widest"
              >
                Hủy
              </button>
              <button
                onClick={confirmDelete}
                className="flex-[2] py-3.5 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-red-700 active:scale-95 shadow-lg shadow-red-100"
              >
                Xác nhận xóa
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] w-full max-w-sm overflow-hidden shadow-2xl animate-modalUp p-6 sm:p-8 text-center border border-gray-100">
            <div className={`w-16 h-16 ${bulkAction === "hide" ? "bg-amber-50 text-amber-500 border-amber-100" : "bg-emerald-50 text-emerald-500 border-emerald-100"} rounded-2xl flex items-center justify-center mx-auto mb-6 border-2`}>
              <span className="material-symbols-outlined text-[32px]">
                {bulkAction === "hide" ? "visibility_off" : "visibility"}
              </span>
            </div>
            <h3 className="text-lg font-black text-slate-800 mb-2 uppercase tracking-widest">
              Xác nhận thay đổi?
            </h3>
            <p className="text-xs sm:text-sm text-slate-400 font-bold mb-8 leading-relaxed">
              Bạn có chắc chắn muốn <span className={bulkAction === "hide" ? "text-amber-500" : "text-emerald-500"}>
                {bulkAction === "hide" ? "ẨN TẤT CẢ" : "HIỆN TẤT CẢ"}
              </span> sản phẩm? Hành động này sẽ ảnh hưởng đến toàn bộ kho hàng.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkModal(false)}
                className="flex-1 py-3 text-slate-400 font-black text-[10px] uppercase tracking-widest"
              >
                Hủy
              </button>
              <button
                onClick={() => handleBulkToggleVisibility(bulkAction)}
                disabled={loading}
                className={`flex-[2] py-3.5 ${bulkAction === "hide" ? "bg-amber-500 hover:bg-amber-600 shadow-amber-100" : "bg-emerald-500 hover:bg-emerald-600 shadow-emerald-100"} text-white rounded-xl font-black text-[10px] uppercase tracking-widest active:scale-95 shadow-lg flex items-center justify-center gap-2`}
              >
                {loading && <span className="material-symbols-outlined animate-spin text-[16px]">refresh</span>}
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
